import React, { useEffect, useRef, useState } from "react";
import "./EpgTimeline.css";

const BASE_OFFSET = 3; // data stored in UTC+3

// Per-channel color palette — each channel gets a distinct color
const CHANNEL_COLORS = [
  { bg: "linear-gradient(135deg, #6366f1, #4f46e5)", border: "rgba(99,102,241,0.4)",  accent: "#6366f1" },
  { bg: "linear-gradient(135deg, #10b981, #059669)", border: "rgba(16,185,129,0.4)",  accent: "#10b981" },
  { bg: "linear-gradient(135deg, #f59e0b, #d97706)", border: "rgba(245,158,11,0.4)",  accent: "#f59e0b" },
  { bg: "linear-gradient(135deg, #ef4444, #dc2626)", border: "rgba(239,68,68,0.4)",   accent: "#ef4444" },
  { bg: "linear-gradient(135deg, #8b5cf6, #7c3aed)", border: "rgba(139,92,246,0.4)",  accent: "#8b5cf6" },
  { bg: "linear-gradient(135deg, #06b6d4, #0891b2)", border: "rgba(6,182,212,0.4)",   accent: "#06b6d4" },
  { bg: "linear-gradient(135deg, #f97316, #ea580c)", border: "rgba(249,115,22,0.4)",  accent: "#f97316" },
  { bg: "linear-gradient(135deg, #ec4899, #db2777)", border: "rgba(236,72,153,0.4)",  accent: "#ec4899" },
];

/**
 * EpgTimeline
 *
 * Props:
 *  - epgByChannelId: { [channelId]: epgBlock[] }
 *  - selectedChannelIds: string[]
 *  - channelLabelById: { [channelId]: string }
 *  - startMinute: number  (0–1439)
 *  - endMinute: number    (0–1439)
 *  - timezoneOffsetHours: number (e.g. 3 for UTC+3)
 *  - selectedDate: string (YYYY-MM-DD)
 *  - isDark: boolean
 */
export default function EpgTimeline({
  epgByDateAndChannel = {},
  selectedChannelIds = [],
  channelLabelById = {},
  channelColorById = {},
  startMinute = 0,
  endMinute = 1439,
  timezoneOffsetHours = 3,
  selectedDate,
  onDateNeeded,
  onDateChange,
  onTimeWindowChange,
  onProgramClick,
  isLoading = false,
  isDark = false,
}) {
  const [viewDate, setViewDate] = useState(selectedDate);
  const [popup, setPopup] = useState(null);
  
  useEffect(() => {
    setViewDate(selectedDate);
  }, [selectedDate]);
  const now = new Date();
  const localDate = new Date(now.getTime() + timezoneOffsetHours * 60 * 60 * 1000);
  const today = localDate.toISOString().slice(0, 10);
  const isToday = viewDate === today;

  const WINDOW_MINUTES = 6 * 60;
  const STEP_MINUTES = 6 * 60;

  const [windowStart, setWindowStart] = useState(0);

  useEffect(() => {
      const now = new Date();
      const utcMinute = now.getUTCHours() * 60 + now.getUTCMinutes();
      const currentMinute = ((utcMinute + timezoneOffsetHours * 60) % 1440 + 1440) % 1440;
      const centeredStart = currentMinute - (6 * 60) / 2;
      const snapped = Math.round(centeredStart / 60) * 60;
      const finalStart = Math.max(0, Math.min(1440 - 6 * 60, snapped));
      setWindowStart(finalStart);
      onTimeWindowChange?.(finalStart, finalStart + 6 * 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timezoneOffsetHours]);
  const dragRef = useRef(null);
  const laneRef = useRef(null);


  const windowEnd = Math.min(windowStart + WINDOW_MINUTES, endMinute);
  const visibleStart = windowStart;
  const visibleEnd = windowEnd;
  const visibleTotal = visibleEnd - visibleStart;

    const canGoBack = true;
    const canGoForward = true;

    const goBack = () => {
    const next = windowStart - STEP_MINUTES;
    if (next < 0) {
      const prevDate = new Date(viewDate);
      prevDate.setDate(prevDate.getDate() - 1);
      const prevStr = prevDate.toISOString().slice(0, 10);
      setViewDate(prevStr);
      onDateNeeded?.(prevStr);
      onDateChange?.(prevStr);
    const newStart = Math.round((next + 1440) / 60) * 60;
      setWindowStart(newStart);
      onTimeWindowChange?.(newStart, newStart + WINDOW_MINUTES);
    } else {
      setWindowStart(next);
      onTimeWindowChange?.(next, next + WINDOW_MINUTES);
    }
  };

  const goForward = () => {
    const next = windowStart + STEP_MINUTES;
    if (next + WINDOW_MINUTES > 1440) {
      const nextDay = new Date(viewDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextStr = nextDay.toISOString().slice(0, 10);
      setViewDate(nextStr);
      onDateNeeded?.(nextStr);
      onDateChange?.(nextStr);
      const newStart = Math.round((next - 1440) / 60) * 60;
      setWindowStart(newStart);
      onTimeWindowChange?.(newStart, newStart + WINDOW_MINUTES);
    } else {
      setWindowStart(next);
      onTimeWindowChange?.(next, next + WINDOW_MINUTES);
    }
  };

    const onMouseDown = (e) => {
        dragRef.current = { startX: e.clientX, startWindow: windowStart };
    };

const onMouseMove = (e) => {
        if (!dragRef.current || !laneRef.current) return;
        const laneWidth = laneRef.current.getBoundingClientRect().width;
        const deltaX = e.clientX - dragRef.current.startX;
        const deltaMinutes = -(deltaX / laneWidth) * WINDOW_MINUTES;
        const rawStart = dragRef.current.startWindow + deltaMinutes;
        // Allow slight overflow so user feels the drag, but not too much
        const clamped = Math.max(-WINDOW_MINUTES, Math.min(1440, rawStart));
        setWindowStart(Math.round(clamped));
    };

   const onMouseUp = () => {
        if (!dragRef.current) return;
        const snapped = Math.round(windowStart / 60) * 60;
        if (snapped < 0) {
          goBack();
        } else if (snapped + WINDOW_MINUTES > 1440) {
          goForward();
        } else {
          const clamped = Math.max(0, Math.min(1440 - WINDOW_MINUTES, snapped));
          setWindowStart(clamped);
          onTimeWindowChange?.(clamped, clamped + WINDOW_MINUTES);
        }
        dragRef.current = null;
    };

    const onMouseLeave = () => {
        if (dragRef.current) onMouseUp();
    };

    // Touch support
    const onTouchStart = (e) => {
        dragRef.current = { startX: e.touches[0].clientX, startWindow: windowStart };
    };

    const onTouchMove = (e) => {
        if (!dragRef.current || !laneRef.current) return;
        const laneWidth = laneRef.current.getBoundingClientRect().width;
        const deltaX = e.touches[0].clientX - dragRef.current.startX;
        const deltaMinutes = -(deltaX / laneWidth) * WINDOW_MINUTES;
        const rawStart = dragRef.current.startWindow + deltaMinutes;
        const clamped = Math.max(-WINDOW_MINUTES, Math.min(1440, rawStart));
        setWindowStart(Math.round(clamped));
    };

    const onTouchEnd = () => onMouseUp();

  const [nowMinute, setNowMinute] = useState(() =>
    getCurrentTzMinute(timezoneOffsetHours)
  );

  useEffect(() => {
    if (!isToday) return;
    setNowMinute(getCurrentTzMinute(timezoneOffsetHours));
    const interval = setInterval(() => {
      setNowMinute(getCurrentTzMinute(timezoneOffsetHours));
    }, 30000);
    return () => clearInterval(interval);
  }, [isToday, timezoneOffsetHours]);

    const nowPct =
        isToday && nowMinute >= visibleStart && nowMinute <= visibleEnd
        ? ((nowMinute - visibleStart) / visibleTotal) * 100
        : null;

    // Hour ticks within the visible window only
// Hour ticks within the visible window only
    const hourTicks = [];
    const startHour = Math.ceil(visibleStart / 60);
    const endHour = Math.floor(visibleEnd / 60);
    for (let h = startHour; h <= endHour; h++) {
        const pct = ((h * 60 - visibleStart) / visibleTotal) * 100;
        const displayH = ((h % 24) + 24) % 24; // wrap negative hours
        hourTicks.push({ h, pct, displayH });
    }

  const shiftMinutes = (BASE_OFFSET - timezoneOffsetHours) * 60;

  const rows = selectedChannelIds.map((channelId, colorIndex) => {
    const blocks = epgByDateAndChannel[viewDate]?.[channelId] || [];
    const label = channelLabelById[channelId] || channelId;
    const channelHex = channelColorById[channelId];
    const color = channelHex
      ? {
          bg: channelHex,
          border: `${channelHex}66`,
          accent: channelHex,
        }
      : CHANNEL_COLORS[colorIndex % CHANNEL_COLORS.length];

    const visibleBlocks = blocks
      .map((b) => {
        const s = wrapMinute(b.startMinute - shiftMinutes);
        const e = wrapMinute(b.endMinute - shiftMinutes);
        return { ...b, shiftedStart: s, shiftedEnd: e };
      })
      .filter((b) => b.shiftedEnd > visibleStart && b.shiftedStart < visibleEnd);

    return { channelId, label, blocks: visibleBlocks, color };
  });

  // Don't render if no channels have any EPG data
    if (!selectedChannelIds.length) return null;

  return (
   <div
      className={`epgt-wrapper ${isDark ? "epgt-dark" : "epgt-light"}`}
      onClick={() => setPopup(null)}
    >

        {/* Header: title + navigation arrows */}
        <div className="epgt-nav-row">
        <div className="epgt-accent-spacer" />
        <div className="epgt-nav-controls">
          <button
            className="epgt-nav-btn"
            onClick={goBack}
            disabled={!canGoBack}
            title="Previous 6 hours"
          >
            ‹
          </button>
          <span className="epgt-nav-range">
            {viewDate} · {formatMinute(visibleStart)} – {formatMinute(visibleEnd)}
          </span>
          <button
            className="epgt-nav-btn"
            onClick={goForward}
            disabled={!canGoForward}
            title="Next 6 hours"
          >
            ›
          </button>
          <button
            className="epgt-live-btn"
            onClick={() => {
              const now = new Date();
              const localDate = new Date(now.getTime() + timezoneOffsetHours * 60 * 60 * 1000);
            const todayStr = localDate.toISOString().slice(0, 10);
              setViewDate(todayStr);
              onDateNeeded?.(todayStr);
              onDateChange?.(todayStr);
              const utcMinute = now.getUTCHours() * 60 + now.getUTCMinutes();
              const currentMinute = ((utcMinute + timezoneOffsetHours * 60) % 1440 + 1440) % 1440;
              const centeredStart = currentMinute - (6 * 60) / 2;
              const snapped = Math.round(centeredStart / 60) * 60;
              const finalStart = Math.max(0, Math.min(1440 - 6 * 60, snapped));
              setWindowStart(finalStart);
              onDateChange?.(todayStr);
              onTimeWindowChange?.(finalStart, finalStart + WINDOW_MINUTES);
            }}
            title="Jump to current time"
          >
            ● LIVE
          </button>
        </div>
      </div>

        <div className="epgt-ruler-row">
        <div className="epgt-accent-spacer" />
            <div className="epgt-ruler">
          {hourTicks.map(({ h, pct, displayH }) => (
            <div key={h} className="epgt-ruler-tick" style={{ left: `${pct}%` }}>
              {String(displayH).padStart(2, "0")}:00
            </div>
          ))}
        </div>
      </div>

      {/* Channel rows */}
            <div
                className="epgt-rows"
                ref={laneRef}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseLeave}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={{ cursor: dragRef.current ? "grabbing" : "grab" }}
            >
        {rows.map(({ channelId, label, blocks, color }) => (
          <div key={channelId} className="epgt-row">

            {/* Channel name label */}
            <div className="epgt-accent-bar" style={{ background: color.accent }} title={label} />


            {/* Program lane */}
            <div className="epgt-lane" title={label}>

              {/* Vertical grid lines */}
              {hourTicks.map(({ h, pct }) => (
                <div key={h} className="epgt-grid-line" style={{ left: `${pct}%` }} />
              ))}

              {/* Program blocks */}
              {isLoading && blocks.length === 0 && (
                <>
                  <div className={`epgt-skeleton ${isDark ? "epgt-skeleton-dark" : "epgt-skeleton-light"}`} style={{ left: "0%", width: "28%" }} />
                  <div className={`epgt-skeleton ${isDark ? "epgt-skeleton-dark" : "epgt-skeleton-light"}`} style={{ left: "30%", width: "22%" }} />
                  <div className={`epgt-skeleton ${isDark ? "epgt-skeleton-dark" : "epgt-skeleton-light"}`} style={{ left: "54%", width: "18%" }} />
                  <div className={`epgt-skeleton ${isDark ? "epgt-skeleton-dark" : "epgt-skeleton-light"}`} style={{ left: "74%", width: "24%" }} />
                </>
              )}

              {/* Program blocks */}
              {blocks.map((b) => {
                const clampedStart = Math.max(b.shiftedStart, visibleStart);
                const clampedEnd = Math.min(b.shiftedEnd, visibleEnd);
                const leftPct = ((clampedStart - visibleStart) / visibleTotal) * 100;
                const widthPct = Math.max(0.3, ((clampedEnd - clampedStart) / visibleTotal) * 100);

                return (
                  <div
                    key={b.id}
                    className="epgt-block"
                    style={{
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      background: color.bg,
                      borderColor: color.border,
                      borderLeftColor: color.accent,
                    }}
                    title={`${label} / ${b.programName}  ${formatMinute(b.shiftedStart)} – ${formatMinute(b.shiftedEnd)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setPopup({
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                        channelId,
                        label,
                        programName: b.programName,
                        shiftedStart: b.shiftedStart,
                        shiftedEnd: b.shiftedEnd,
                        color: color.accent,
                        totalViews: null, // will be filled by callback
                      });
                      onProgramClick?.({
                        channelId,
                        shiftedStart: b.shiftedStart,
                        shiftedEnd: b.shiftedEnd,
                        viewDate,
                      }, (totalViews) => {
                        setPopup((prev) => prev ? { ...prev, totalViews } : null);
                      });
                    }}
                  >
                    <span className="epgt-block-name">{b.programName}</span>
                  </div>
                );
              })}

              {/* Now line */}
              {nowPct !== null && (
                <div className="epgt-now-line" style={{ left: `${nowPct}%` }} />
              )}
            </div>
          </div>
        ))}
      </div>
        {/* Program info popup */}
      {popup && (
        <div
          className={`epgt-popup ${isDark ? "epgt-popup-dark" : "epgt-popup-light"}`}
          style={{
            position: "fixed",
            left: popup.x,
            top: popup.y - 8,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="epgt-popup-header" style={{ background: popup.color }}>
            <span className="epgt-popup-channel">{popup.label}</span>
            <button className="epgt-popup-close" onClick={() => setPopup(null)}>✕</button>
          </div>
          <div className="epgt-popup-body">
            <div className="epgt-popup-program">{popup.programName}</div>
            <div className="epgt-popup-time">
              {formatMinute(popup.shiftedStart)} – {formatMinute(popup.shiftedEnd)}
            </div>
            <div className="epgt-popup-views">
              {popup.totalViews === null
                ? "Calculating..."
                : `${Number(popup.totalViews).toLocaleString()} total views`}
            </div>
          </div>
        </div>
      )}
      {/* Now time label below all rows */}
      {nowPct !== null && (
        <div className="epgt-now-label-row">
          <div className="epgt-accent-spacer" />
          <div className="epgt-now-label-track">
            <div className="epgt-now-label" style={{ left: `${nowPct}%` }}>
              {formatMinute(nowMinute)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getCurrentTzMinute(tzOffset) {
  const now = new Date();
  const utcMinute = now.getUTCHours() * 60 + now.getUTCMinutes();
  const shifted = utcMinute + tzOffset * 60;
  return wrapMinute(shifted);
}

function wrapMinute(m) {
  return ((m % 1440) + 1440) % 1440;
}

function formatMinute(m) {
  const wrapped = wrapMinute(m);
  const hh = String(Math.floor(wrapped / 60)).padStart(2, "0");
  const mm = String(wrapped % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}
